import React, { useEffect } from 'react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatNumber } from '../utils/formatters';

/**
 * Componente que exibe estatísticas sobre o mapeamento de fossas sépticas
 */
const DashboardEstatisticas = () => {
  const { 
    estatisticas, 
    carregarPropriedades, 
    loading 
  } = useFossasSepticas();

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarPropriedades();
  }, [carregarPropriedades]);

  // Calcular porcentagens
  const calcularPorcentagem = (valor, total) => {
    if (!total) return 0;
    return Math.round((valor / total) * 100);
  };

  const porcentagemMapeadas = calcularPorcentagem(estatisticas.propriedades.mapeadas, estatisticas.propriedades.total);
  const porcentagemComFossa = calcularPorcentagem(estatisticas.residencias.comFossa, estatisticas.residencias.total);

  return (
    <div className="card">
      <h2>Dashboard de Fossas Sépticas</h2>
      
      {loading.propriedades ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ 
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderLeftColor: '#2e7d32',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Carregando estatísticas...</p>
        </div>
      ) : (
        <>
          {/* Cards de estatísticas */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #d6d8db',
              borderLeft: '5px solid #6c757d'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total de Propriedades</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {formatNumber(estatisticas.propriedades.total)}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Propriedades cadastradas</div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #c3e6cb',
              borderLeft: '5px solid #28a745'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Propriedades Mapeadas</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {formatNumber(estatisticas.propriedades.mapeadas)}
                <span style={{ fontSize: '16px', color: '#666', marginLeft: '5px' }}>({porcentagemMapeadas}%)</span>
              </div>
              <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', margin: '10px 0', overflow: 'hidden' }}>
                <div 
                  style={{ 
                    height: '100%', 
                    width: `${porcentagemMapeadas}%`, 
                    backgroundColor: '#28a745',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Propriedades já visitadas e mapeadas</div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #f5c6cb',
              borderLeft: '5px solid #dc3545'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#721c24', fontSize: '16px' }}>Propriedades Não Mapeadas</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {formatNumber(estatisticas.propriedades.naoMapeadas)}
                <span style={{ fontSize: '16px', color: '#666', marginLeft: '5px' }}>
                  ({calcularPorcentagem(estatisticas.propriedades.naoMapeadas, estatisticas.propriedades.total)}%)
                </span>
              </div>
              <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', margin: '10px 0', overflow: 'hidden' }}>
                <div 
                  style={{ 
                    height: '100%', 
                    width: `${calcularPorcentagem(estatisticas.propriedades.naoMapeadas, estatisticas.propriedades.total)}%`, 
                    backgroundColor: '#dc3545',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Propriedades pendentes de mapeamento</div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #d4edda',
              borderLeft: '5px solid #17a2b8'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#0c5460', fontSize: '16px' }}>Com Fossa Séptica</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {formatNumber(estatisticas.residencias.comFossa)}
                <span style={{ fontSize: '16px', color: '#666', marginLeft: '5px' }}>({porcentagemComFossa}%)</span>
              </div>
              <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', margin: '10px 0', overflow: 'hidden' }}>
                <div 
                  style={{ 
                    height: '100%', 
                    width: `${porcentagemComFossa}%`, 
                    backgroundColor: '#17a2b8',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Propriedades com fossa séptica cadastrada</div>
            </div>
          </div>
          
          {/* Distribuição por localidade */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Distribuição por Localidade</h3>
            
            {estatisticas.propriedades.porLocalidade.length === 0 ? (
              <p>Nenhuma localidade com propriedades cadastradas.</p>
            ) : (
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Localidade</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Total</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Mapeadas</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Não Mapeadas</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Progresso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estatisticas.propriedades.porLocalidade.map(loc => (
                      <tr key={loc.id}>
                        <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>{loc.nome}</td>
                        <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>{loc.total}</td>
                        <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>{loc.mapeadas}</td>
                        <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>{loc.naoMapeadas}</td>
                        <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>
                          <div style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
                            <div 
                              style={{ 
                                height: '100%',
                                width: `${calcularPorcentagem(loc.mapeadas, loc.total)}%`, 
                                backgroundColor: '#28a745' 
                              }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DashboardEstatisticas;